import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PickItemStatusBadge, ShortageReasonBadge } from './PickingStatusBadge';
import type { PickingListItem } from '../../types';

interface PickingItemsTableProps {
  items: PickingListItem[];
  onConfirmPick?: (item: PickingListItem) => void;
  onReportShortage?: (item: PickingListItem) => void;
  onSkip?: (item: PickingListItem) => void;
  readOnly?: boolean;
}

export function PickingItemsTable({
  items,
  onConfirmPick,
  onReportShortage,
  onSkip,
  readOnly = false,
}: PickingItemsTableProps) {
  if (items.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No items to pick.
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>SKU</TableHead>
            <TableHead>Serial Number</TableHead>
            <TableHead>From Location</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Notes</TableHead>
            {!readOnly && <TableHead className="w-[200px]"></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={item.id}
              className={item.pick_status === 'picked' ? 'bg-green-50' : item.pick_status === 'short' ? 'bg-orange-50' : ''}
            >
              <TableCell className="text-muted-foreground text-sm">
                {item.sort_order}
              </TableCell>
              <TableCell>
                <div className="font-medium">{item.sku_code || '-'}</div>
                {item.sku_name && (
                  <div className="text-sm text-muted-foreground truncate max-w-[200px]">
                    {item.sku_name}
                  </div>
                )}
              </TableCell>
              <TableCell className="font-mono text-sm">
                {item.serial_numbers?.join(', ') || '-'}
              </TableCell>
              <TableCell>
                <div className="font-mono text-sm">{item.bin_code || '-'}</div>
                {item.zone_code && (
                  <div className="text-xs text-muted-foreground">{item.zone_code}</div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  <PickItemStatusBadge status={item.pick_status} />
                  {item.shortage_reason && (
                    <ShortageReasonBadge reason={item.shortage_reason} />
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                {item.notes || '-'}
              </TableCell>
              {!readOnly && (
                <TableCell>
                  <div className="flex gap-1">
                    {item.pick_status === 'pending' && (
                      <>
                        {onConfirmPick && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => onConfirmPick(item)}
                          >
                            Pick
                          </Button>
                        )}
                        {onReportShortage && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-orange-600"
                            onClick={() => onReportShortage(item)}
                          >
                            Short
                          </Button>
                        )}
                        {onSkip && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onSkip(item)}
                          >
                            Skip
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
